// Portal-V2-Client (2026-06-24) — Hybrid messaging panel for the Concierge
// client portal. Combines:
//   1. Broker contact card (avatar / name / role / phone / email)
//   2. Message thread (oldest first; 30s polling while panel mounted)
//   3. Compose form (textarea + Send) — wires to the new
//      /api/portal/applications/{id}/messages endpoints landed in PR #128.
//
// No fake presence — there's no "● online" because we have no real-time
// pinging. Instead we show "Replies within 1 business hour" which is
// honest and brand-aligned. Each post emails the broker via Resend on the
// backend (best-effort) so they see the request even if they're not in
// the CRM. The broker replies from the new Messages tab on the deal in
// EditableDealPreview (PR Portal-V2-Admin — next PR).
//
// Props:
//   - application:  the active app dict (used for ref + assigned_broker_*
//                   fallback when team_lead isn't populated yet).
//   - brokerHint:   optional { name, email, role } overriding what we
//                   derive from the application. Used in the future when
//                   we want to render multi-member team rather than just
//                   the lead.
//
// Render shape mirrors solara_portal_mockup_3_concierge_board.html.

import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Mail, Phone, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

function initialsOf(name) {
  if (!name) return "??";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    const isYest = d.toDateString() === yesterday.toDateString();
    const timeStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    if (sameDay) return `Today, ${timeStr}`;
    if (isYest) return `Yesterday, ${timeStr}`;
    return d.toLocaleDateString([], { day: "numeric", month: "short" }) + `, ${timeStr}`;
  } catch {
    return String(iso);
  }
}

// HF (2026-06-25) — when a deal hasn't been assigned to a specific broker
// yet, fall back to the Solara general team contact instead of rendering
// the useless "Your broker / Your broker" placeholder. Email always set so
// the client has *some* way to reach us even before assignment.
const DEFAULT_BROKER = {
  name: "Solara Loans Team",
  role: "Your Solara contact",
  email: "loans@sgcap.com.au",
  phone: "",
};

export default function MessagingPanel({ application, brokerHint }) {
  const appId = application?.id;
  const ref = application?.application_reference || "";
  // Detect whether a broker has actually been assigned. The previous
  // "Your broker" fallback masked unassigned deals — clients saw a card
  // with no email/phone and no way to reach anyone. Now we surface the
  // Solara team contact instead.
  const hasAssignedBroker = !!(
    brokerHint?.name
    || application?.assigned_broker_name
    || application?.assigned_broker_email
  );
  const brokerName = hasAssignedBroker
    ? (brokerHint?.name
       || application?.assigned_broker_name
       || application?.assigned_broker_email)
    : DEFAULT_BROKER.name;
  // Email is always populated — if there's no assigned broker email,
  // fall through to the general loans@sgcap.com.au inbox.
  const brokerEmail =
    brokerHint?.email
    || application?.assigned_broker_email
    || DEFAULT_BROKER.email;
  // Phone only renders when present. We don't have a published general
  // line yet, so unassigned deals just hide the Call button.
  const brokerPhone =
    brokerHint?.phone
    || application?.assigned_broker_phone
    || DEFAULT_BROKER.phone;
  const brokerRole = brokerHint?.role
    || (hasAssignedBroker ? "Your broker" : DEFAULT_BROKER.role);

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);

  const loadMessages = useCallback(async () => {
    if (!appId) return;
    try {
      const res = await axios.get(`${API}/portal/applications/${appId}/messages`);
      setMessages(res.data?.messages || []);
    } catch (e) {
      // 403/404 silently — likely permissions issue, no toast spam.
      if (e?.response?.status >= 500) {
        // Only complain if it's a server fault, not auth.
        // (Polling otherwise floods toasts.)
      }
    } finally {
      setLoading(false);
    }
  }, [appId]);

  // Initial load + 30s polling. Reset state when the active app changes.
  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setDraft("");
    loadMessages();
    // PERF-1 (2026-07-08) — skip the poll while the browser tab is hidden.
    const t = setInterval(() => { if (!document.hidden) loadMessages(); }, POLL_MS);
    return () => clearInterval(t);
  }, [appId, loadMessages]);

  // Auto-scroll thread to bottom when new messages arrive.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;
    if (!appId) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/portal/applications/${appId}/messages`,
        { body: text }
      );
      const saved = res.data?.message;
      if (saved) {
        setMessages((prev) => [...prev, saved]);
      } else {
        // Defensive — refetch if the response was unexpected.
        await loadMessages();
      }
      setDraft("");
      toast.success("Message sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send message");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    // Cmd/Ctrl + Enter to send (don't trap plain Enter — multi-line drafts).
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="bg-white border border-[#bd9a59]/30 rounded-xl overflow-hidden">
      {/* Broker contact header */}
      <div className="p-4 border-b border-[#f1f1ec]">
        <div className="flex items-center gap-3">
          {/* Use the Solara spiral icon as the avatar when there's no
              named broker assigned yet — feels branded rather than
              showing arbitrary "SL" initials for the team account. */}
          {hasAssignedBroker ? (
            <div className="w-11 h-11 rounded-full flex items-center justify-center bg-[#021d49] text-[#bd9a59] font-serif text-sm shrink-0">
              {initialsOf(brokerName)}
            </div>
          ) : (
            <div className="w-11 h-11 rounded-full bg-[#021d49] flex items-center justify-center shrink-0 overflow-hidden">
              <img
                src="/logos/solara-icon.png"
                alt=""
                className="w-7 h-7 object-contain"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#021d49] truncate">{brokerName}</div>
            <div className="text-[11px] text-gray-500 truncate">{brokerRole}</div>
          </div>
        </div>
        {(brokerEmail || brokerPhone) && (
          <div className="flex gap-2 mt-3">
            {brokerPhone && (
              <a href={`tel:${brokerPhone}`} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs rounded-md bg-[#021d49] text-white hover:bg-[#052b66]" data-testid="broker-call">
                <Phone className="w-3.5 h-3.5" /> Call
              </a>
            )}
            {brokerEmail && (
              <a href={`mailto:${brokerEmail}`} className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs rounded-md bg-[#bd9a59] text-[#021d49] hover:bg-[#a8854a]" data-testid="broker-email">
                <Mail className="w-3.5 h-3.5" /> Email
              </a>
            )}
          </div>
        )}
        <div className="mt-3 text-[11px] text-gray-500 italic">
          Replies within 1 business hour
        </div>
      </div>

      {/* Message thread */}
      <div
        ref={threadRef}
        className="px-4 py-3 max-h-72 overflow-y-auto bg-[#fafaf9]"
        data-testid="messaging-thread"
      >
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-4 h-4 text-[#bd9a59] animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-6 text-[12px] text-gray-400">
            No messages yet. Send your broker a note below — they'll receive an email and reply here.
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((m) => (
              <div key={m.id} data-testid={`message-${m.id}`}>
                <div className={`text-[11px] text-gray-400 mb-1 ${m.sender_is_admin ? "" : "text-right"}`}>
                  {m.sender_name || (m.sender_is_admin ? "Broker" : "You")} · {formatTime(m.created_at)}
                </div>
                <div
                  className={
                    "rounded-lg px-3 py-2 text-[13px] whitespace-pre-wrap break-words leading-relaxed " +
                    (m.sender_is_admin
                      ? "bg-white border border-[#f1f1ec] text-[#021d49]"
                      : "bg-[#021d49] text-white ml-6")
                  }
                >
                  {m.body}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Compose form */}
      <div className="p-3 border-t border-[#f1f1ec] bg-white">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={`Send a message about ${ref || "this application"}…`}
          rows={2}
          disabled={sending}
          className="w-full text-[13px] px-3 py-2 border border-gray-200 rounded-md outline-none focus:border-[#bd9a59] resize-none disabled:opacity-60"
          data-testid="messaging-draft"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-gray-400">⌘/Ctrl + Enter to send</span>
          <button
            type="button"
            onClick={handleSend}
            disabled={sending || !draft.trim()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#021d49] text-white rounded-md hover:bg-[#052b66] disabled:opacity-50"
            data-testid="messaging-send"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
